import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Sparkles, Loader2, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/admin/AdminUI";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const emptyFrom = (fields) => {
  const o = {};
  fields.forEach((f) => { o[f.name] = f.type === "list" ? [] : (f.default ?? ""); });
  o.status = "draft";
  return o;
};

export default function ResourceManager({ collection, title, subtitle, columns, fields, aiKind, statusField = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState("");
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/${collection}?admin=true`).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [collection]);

  const openNew = () => { setEditing(null); setForm(emptyFrom(fields)); setDialogOpen(true); };
  const openEdit = (item) => {
    const f = { ...item };
    fields.forEach((fl) => { if (fl.type === "list" && !Array.isArray(f[fl.name])) f[fl.name] = f[fl.name] ? [f[fl.name]] : []; });
    setEditing(item); setForm(f); setDialogOpen(true);
  };

  const set = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) { await api.put(`/admin/${collection}/${editing.id}`, payload); toast.success("Modifié avec succès"); }
      else { await api.post(`/admin/${collection}`, payload); toast.success("Créé avec succès"); }
      setDialogOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/${collection}/${id}`); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    try { await api.put(`/admin/${collection}/${item.id}`, { status: next }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const runAI = async (field) => {
    const topic = form.name || form.title || form[field.aiFrom] || "";
    if (!topic) { toast.error("Renseignez d'abord un nom / titre."); return; }
    setAiLoading(field.name);
    try {
      const { data } = await api.post("/admin/ai/generate", { prompt: topic, kind: aiKind });
      set(field.name, data.content);
      toast.success("Contenu généré par l'IA");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiLoading(""); }
  };

  const filtered = items.filter((it) => {
    if (!search) return true;
    const hay = `${it.name || ""} ${it.title || ""} ${it.reference || ""}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}
        action={<button onClick={openNew} data-testid={`${collection}-add-button`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold hover:bg-[#2563EB]"><Plus className="w-4 h-4" /> Ajouter</button>} />

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="pl-9 bg-[#111827] border-[#1E293B]" />
      </div>

      <Card className="overflow-hidden">
        {loading ? <EmptyState text="Chargement…" /> : filtered.length === 0 ? <EmptyState text="Aucun élément. Cliquez sur Ajouter pour commencer." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${collection}-table`}>
              <thead><tr className="border-b border-[#1E293B] text-left text-slate-400">
                {columns.map((c) => <th key={c.key} className="px-4 py-3 font-medium">{c.label}</th>)}
                {statusField && <th className="px-4 py-3 font-medium">Statut</th>}
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="border-b border-[#1E293B]/60 hover:bg-[#0B0F17]/50">
                    {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-200">{c.render ? c.render(item) : (String(item[c.key] ?? "—").slice(0, 60))}</td>)}
                    {statusField && <td className="px-4 py-3"><button onClick={() => togglePublish(item)} title="Basculer publication"><StatusBadge status={item.status} /></button></td>}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(item)} data-testid={`${collection}-edit-${item.id}`} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#1E293B]"><Pencil className="w-4 h-4" /></button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><button data-testid={`${collection}-delete-${item.id}`} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-[#1E293B]"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                          <AlertDialogContent className="dark bg-[#111827] border-[#1E293B]">
                            <AlertDialogHeader><AlertDialogTitle className="text-white">Supprimer ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => remove(item.id)} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="dark bg-[#111827] border-[#1E293B] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing ? "Modifier" : "Nouveau"} · {title}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <div key={f.name}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-slate-300">{f.label}</label>
                  {f.ai && <button onClick={() => runAI(f)} disabled={aiLoading === f.name} className="inline-flex items-center gap-1.5 text-xs text-[#4CB944] hover:text-[#3AA033] font-medium">{aiLoading === f.name ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Générer avec l'IA</button>}
                </div>
                {f.type === "textarea" || f.type === "richtext" ? (
                  <Textarea value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} rows={f.type === "richtext" ? 8 : 3} className="bg-[#0B0F17] border-[#1E293B] text-white" placeholder={f.placeholder} />
                ) : f.type === "select" ? (
                  <select value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-white text-sm">
                    <option value="">— Choisir —</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "list" ? (
                  <Textarea value={(Array.isArray(form[f.name]) ? form[f.name] : []).join("\n")} onChange={(e) => set(f.name, e.target.value.split("\n").filter(Boolean))} rows={3} className="bg-[#0B0F17] border-[#1E293B] text-white" placeholder="Un élément par ligne" />
                ) : f.type === "boolean" ? (
                  <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={!!form[f.name]} onChange={(e) => set(f.name, e.target.checked)} /> Oui</label>
                ) : (
                  <Input value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className="bg-[#0B0F17] border-[#1E293B] text-white" placeholder={f.placeholder} />
                )}
              </div>
            ))}
            {statusField && (
              <div><label className="text-sm font-medium text-slate-300 mb-1.5 block">Statut</label>
                <select value={form.status || "draft"} onChange={(e) => set("status", e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-white text-sm">
                  <option value="draft">Brouillon</option><option value="published">Publié</option><option value="archived">Archivé</option>
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2.5 rounded-xl border border-[#1E293B] text-slate-300 text-sm">Annuler</button>
            <button onClick={save} disabled={saving} data-testid={`${collection}-save-button`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold hover:bg-[#2563EB] disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />}Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
